import { User as FirebaseUser, getAuth } from 'firebase/auth';
import { jwtDecode } from 'jwt-decode';
import * as React from 'react';

type CustomClaims = {
  roles?: { id: string; name: string }[];
  organization?: { id: string; name: string };
};

type User =
  | (FirebaseUser &
      CustomClaims & {
        loading: boolean;
        user?: FirebaseUser;
      })
  | null;

type ContextState = { user: User };

interface FirebaseAuthProviderProps {
  children: React.ReactNode;
}

const FirebaseAuthContext = React.createContext<ContextState | undefined>(
  undefined,
);

const FirebaseAuthProvider: React.FC<FirebaseAuthProviderProps> = ({
  children,
}: FirebaseAuthProviderProps) => {
  const [user, setUser] = React.useState<User>(null);

  React.useEffect(() => {
    const unsubscribe = getAuth().onIdTokenChanged(async (user) => {
      if (!user) {
        setUser(Object.assign({}, { loading: false }) as unknown as User);
        return;
      }

      const decodedToken = jwtDecode<CustomClaims>(await user.getIdToken());

      setUser(
        Object.assign({}, user, {
          roles: decodedToken.roles,
          organization: decodedToken.organization,
          loading: false,
          user,
        }),
      );
    });

    return unsubscribe;
  }, []);

  return (
    <FirebaseAuthContext.Provider value={{ user }}>
      {children}
    </FirebaseAuthContext.Provider>
  );
};

function useFirebaseAuth() {
  const context = React.useContext(FirebaseAuthContext);
  if (context === undefined) {
    throw new Error(
      'useFirebaseAuth must be used within a FirebaseAuthProvider',
    );
  }

  return context.user;
}

export { FirebaseAuthProvider, useFirebaseAuth };
